import { useCallback, useState } from 'react'
import apiService from '../../../services/ApiService'
import { exportAdminSubscriptionsToCSV } from '../utils/subscriptionExportUtils'

const PAGE_SIZE = 20

export const useSubscriptionsManagement = () => {
  const [subscriptions, setSubscriptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [statusCounts, setStatusCounts] = useState({})

  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')

  const buildParams = useCallback(
    (page, overrides = {}) => {
      const targetPage = page ?? currentPage
      const merged = {
        searchTerm,
        statusFilter,
        fromDate,
        toDate,
        ...overrides,
      }

      return {
        page: Math.max(0, targetPage - 1),
        size: merged.size || PAGE_SIZE,
        search: merged.searchTerm?.trim() || undefined,
        status: merged.statusFilter || 'ALL',
        fromDate: merged.fromDate || undefined,
        toDate: merged.toDate || undefined,
        sortBy: merged.sortBy || 'createdAt',
        sortDir: merged.sortDir || 'desc',
      }
    },
    [currentPage, fromDate, searchTerm, statusFilter, toDate]
  )

  const loadSubscriptions = useCallback(
    async (page = currentPage, overrides = {}) => {
      try {
        setLoading(true)
        const params = buildParams(page, overrides)
        const response = await apiService.getAdminSubscriptions(params)

        setSubscriptions(response?.subscriptions || [])
        setTotalPages(response?.totalPages || 1)
        setTotalItems(response?.totalItems || 0)
        setStatusCounts(response?.statusCounts || {})
      } catch (error) {
        console.error('Failed to load subscriptions:', error)
        throw error
      } finally {
        setLoading(false)
      }
    },
    [buildParams, currentPage]
  )

  const applyFilters = useCallback(async () => {
    setCurrentPage(1)
    await loadSubscriptions(1)
  }, [loadSubscriptions])

  const resetFilters = useCallback(async () => {
    setSearchTerm('')
    setStatusFilter('ALL')
    setFromDate('')
    setToDate('')
    setCurrentPage(1)
    await loadSubscriptions(1, {
      searchTerm: '',
      statusFilter: 'ALL',
      fromDate: '',
      toDate: '',
    })
  }, [loadSubscriptions])

  const handlePageChange = useCallback(
    async (page) => {
      setCurrentPage(page)
      await loadSubscriptions(page)
    },
    [loadSubscriptions]
  )

  const exportSubscriptions = useCallback(async () => {
    try {
      setExporting(true)
      const response = await apiService.getAdminSubscriptions(
        buildParams(1, { size: 5000 })
      )
      const rows = response?.subscriptions || []
      exportAdminSubscriptionsToCSV(rows)
      return rows.length
    } finally {
      setExporting(false)
    }
  }, [buildParams])

  return {
    subscriptions,
    loading,
    exporting,
    currentPage,
    totalPages,
    totalItems,
    statusCounts,
    searchTerm,
    statusFilter,
    fromDate,
    toDate,
    setSearchTerm,
    setStatusFilter,
    setFromDate,
    setToDate,
    loadSubscriptions,
    applyFilters,
    resetFilters,
    handlePageChange,
    exportSubscriptions,
  }
}
